import React, { useState, useRef } from 'react';
import { createChangeOrder } from '../api';
import { useToast } from '../App';
import { useAuth } from '../context/AuthContext';
import { X, ChevronRight, ChevronLeft, Check, Download } from './Icons';
import { generateChangeOrderPDF } from './ChangeOrderPDF';
import './ChangeOrderPanel.css';

const PAYMENT_TERMS = [
  { value: '50% upfront, 50% on delivery', label: '50% upfront, 50% on delivery' },
  { value: '100% upfront before work begins', label: '100% upfront before work begins' },
  { value: '100% on delivery', label: '100% on delivery' },
  { value: 'Net 15 — due 15 days after invoice', label: 'Net 15' },
  { value: 'Net 30 — due 30 days after invoice', label: 'Net 30' },
  { value: 'custom', label: 'Custom…' },
];

// ── Utility ────────────────────────────────────────────────────────────────────
function fmtMoney(amount, currency = 'USD') {
  if (amount == null || isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
}

function addDays(dateStr, days) {
  const d = dateStr ? new Date(dateStr) : new Date();
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().split('T')[0];
}

// ── Step indicator ─────────────────────────────────────────────────────────────
function Steps({ current }) {
  const labels = ['Work Details', 'Client & Payment', 'Preview & Create'];
  return (
    <div className="cop-steps" aria-label="Change order creation steps">
      {labels.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'active' : 'pending';
        return (
          <React.Fragment key={i}>
            <div className={`cop-step cop-step-${state}`}>
              <div className="cop-step-circle">
                {state === 'done' ? <Check size={12} /> : <span>{i + 1}</span>}
              </div>
              <span className="cop-step-label">{label}</span>
            </div>
            {i < labels.length - 1 && <div className={`cop-step-line ${i < current ? 'done' : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Step 1: Work Details ───────────────────────────────────────────────────────
function Step1({ form, onChange }) {
  const computed = form.pricing_type === 'hourly'
    ? (parseFloat(form.hours) || 0) * (parseFloat(form.rate) || 0)
    : parseFloat(form.price) || 0;

  const deadline = form.timeline_days
    ? addDays(form.base_deadline, form.timeline_days)
    : null;

  return (
    <div className="cop-step-body">
      <div className="form-group">
        <label className="form-label">Change Title <span className="form-req">*</span></label>
        <input
          className="form-input"
          placeholder="e.g. Mobile app version, Dark mode feature"
          value={form.title}
          onChange={e => onChange('title', e.target.value)}
          maxLength={120}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-input"
          placeholder="Describe what additional work is involved in this change…"
          value={form.description}
          onChange={e => onChange('description', e.target.value)}
          rows={3}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Pricing Type</label>
        <div className="cop-type-toggle">
          {[['fixed', 'Fixed Fee'], ['hourly', 'Hourly']].map(([val, label]) => (
            <button
              key={val}
              type="button"
              className={`cop-type-btn ${form.pricing_type === val ? 'active' : ''}`}
              onClick={() => onChange('pricing_type', val)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {form.pricing_type === 'fixed' ? (
        <div className="form-group">
          <label className="form-label">Amount ({form.currency})</label>
          <div className="rate-input-wrapper">
            <span className="rate-currency-symbol">{form.currency}</span>
            <input
              className="form-input rate-input"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={form.price}
              onChange={e => onChange('price', e.target.value)}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="cop-hourly-row">
            <div className="form-group">
              <label className="form-label">Hours</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.5"
                placeholder="0"
                value={form.hours}
                onChange={e => onChange('hours', e.target.value)}
              />
            </div>
            <div className="cop-rate-symbol">×</div>
            <div className="form-group">
              <label className="form-label">Rate ({form.currency}/hr)</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="1"
                placeholder={form.default_rate || '50'}
                value={form.rate}
                onChange={e => onChange('rate', e.target.value)}
              />
            </div>
          </div>
          <div className="cop-computed-total">
            <span className="cop-computed-total-label">Estimated total</span>
            <span className="cop-computed-total-val">{fmtMoney(computed, form.currency)}</span>
          </div>
        </>
      )}

      <div className="cop-timeline-row">
        <div className="form-group">
          <label className="form-label">Additional Days</label>
          <input
            className="form-input"
            type="number"
            min="0"
            placeholder="0"
            value={form.timeline_days}
            onChange={e => onChange('timeline_days', e.target.value)}
          />
        </div>
        {Number(form.timeline_days) > 0 && deadline && (
          <div className="form-group">
            <label className="form-label">New Deadline</label>
            <input
              className="form-input"
              type="date"
              value={form.new_deadline || deadline}
              onChange={e => onChange('new_deadline', e.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Step 2: Client & Payment ───────────────────────────────────────────────────
function Step2({ form, onChange }) {
  const [customTerms, setCustomTerms] = useState(false);

  const isCustom = !PAYMENT_TERMS.slice(0, -1).some(t => t.value === form.payment_terms);

  function handleTermsChange(val) {
    if (val === 'custom') {
      setCustomTerms(true);
      onChange('payment_terms', '');
    } else {
      setCustomTerms(false);
      onChange('payment_terms', val);
    }
  }

  return (
    <div className="cop-step-body">
      <div className="cop-section-heading">Client Details</div>
      <div className="cop-fields-row">
        <div className="form-group">
          <label className="form-label">Client Name</label>
          <input
            className="form-input"
            placeholder="Sarah Lee"
            value={form.client_name}
            onChange={e => onChange('client_name', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Client Company</label>
          <input
            className="form-input"
            placeholder="Acme Corp (optional)"
            value={form.client_company}
            onChange={e => onChange('client_company', e.target.value)}
          />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Client Email</label>
        <input
          className="form-input"
          type="email"
          placeholder="sarah@acmecorp.com (optional)"
          value={form.client_email}
          onChange={e => onChange('client_email', e.target.value)}
        />
      </div>

      <div className="cop-section-heading" style={{ marginTop: 'var(--space-5)' }}>Payment Terms</div>
      <div className="cop-terms-grid">
        {PAYMENT_TERMS.map(t => (
          <button
            key={t.value}
            type="button"
            className={`cop-terms-btn ${t.value === 'custom'
                ? (isCustom ? 'active' : '')
                : (form.payment_terms === t.value && !isCustom ? 'active' : '')
              }`}
            onClick={() => handleTermsChange(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {(customTerms || isCustom) && (
        <div className="form-group" style={{ marginTop: 'var(--space-3)' }}>
          <label className="form-label">Custom payment terms</label>
          <input
            className="form-input"
            placeholder="e.g. 30% upfront, 40% mid-project, 30% on delivery"
            value={isCustom ? form.payment_terms : ''}
            onChange={e => onChange('payment_terms', e.target.value)}
            autoFocus={customTerms}
          />
        </div>
      )}

      <div className="form-group" style={{ marginTop: 'var(--space-4)' }}>
        <label className="form-label">Internal Notes <span className="form-hint-inline">(not shown on PDF)</span></label>
        <textarea
          className="form-input"
          placeholder="Private notes for your records…"
          value={form.notes}
          onChange={e => onChange('notes', e.target.value)}
          rows={2}
        />
      </div>
    </div>
  );
}

// ── Step 3: Preview ────────────────────────────────────────────────────────────
function Step3({ form, allCOs, project, branding, useBranding, onToggleBranding, creating, onConfirm }) {
  const computed = form.pricing_type === 'hourly'
    ? (parseFloat(form.hours) || 0) * (parseFloat(form.rate) || 0)
    : parseFloat(form.price) || 0;

  const coNum = (allCOs?.length || 0) + 1;

  const totalApproved = (allCOs || [])
    .filter(co => co.status === 'approved')
    .reduce((s, co) => s + (co.pricing_type === 'hourly' ? (co.hours || 0) * (co.rate || 0) : (co.price || 0)), 0);

  return (
    <div className="cop-step-body cop-preview-body">
      <div className="cop-preview-notice">
        Review the details below — this is how your PDF will look.
      </div>

      {/* Branding toggle */}
      {branding && (
        <div className="cop-branding-toggle-row">
          <div className="cop-branding-toggle-info">
            <div className="cop-branding-toggle-label">Include your branding on the PDF</div>
            <div className="cop-branding-toggle-sub">
              {branding.branding_company_name || branding.name || 'Your company name'}
              {branding.branding_logo_url ? ' · logo included' : ' · no logo set — add one in Settings'}
            </div>
          </div>
          <button
            type="button"
            className={`cop-toggle-btn ${useBranding ? 'on' : 'off'}`}
            onClick={onToggleBranding}
            aria-label="Toggle branding"
          >
            <span className="cop-toggle-knob" />
          </button>
        </div>
      )}

      {/* Mini document preview */}
      <div className="cop-doc-preview">
        {/* Header */}
        <div className="cop-doc-header">
          {branding?.branding_logo_url ? (
            <img src={branding.branding_logo_url} alt="Logo" className="cop-doc-logo" />
          ) : (
            <div className="cop-doc-logo-placeholder">{(branding?.name || 'F').slice(0, 1).toUpperCase()}</div>
          )}
          <div className="cop-doc-header-right">
            <div className="cop-doc-company">{branding?.branding_company_name || branding?.name || 'Your Company'}</div>
            {branding?.branding_address && <div className="cop-doc-meta">{branding.branding_address}</div>}
            {(branding?.branding_phone || branding?.branding_website) && (
              <div className="cop-doc-meta">
                {[branding.branding_phone, branding.branding_website].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        </div>

        <div className="cop-doc-divider" />

        <div className="cop-doc-co-num">CHANGE ORDER NO. {String(coNum).padStart(3, '0')}</div>
        <div className="cop-doc-project">Project: <strong>{project?.projectName || '—'}</strong></div>
        <div className="cop-doc-date">Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>

        {(form.client_name || form.client_company || form.client_email) && (
          <>
            <div className="cop-doc-divider" />
            <div className="cop-doc-section-title">CLIENT</div>
            <div className="cop-doc-parties">
              {[form.client_name, form.client_company, form.client_email].filter(Boolean).join(' · ')}
            </div>
          </>
        )}

        <div className="cop-doc-divider" />
        <div className="cop-doc-section-title">DESCRIPTION OF CHANGE</div>
        <div className="cop-doc-change-title">{form.title || '(untitled)'}</div>
        {form.description && <div className="cop-doc-change-desc">{form.description}</div>}

        <div className="cop-doc-divider" />
        <div className="cop-doc-section-title">FINANCIAL IMPACT</div>
        <div className="cop-doc-finance-grid">
          <span>Pricing</span>
          <span>{form.pricing_type === 'hourly' ? `${form.hours || 0} hrs × ${fmtMoney(form.rate, form.currency)}/hr` : 'Fixed Fee'}</span>
          <span>Amount</span>
          <strong>{fmtMoney(computed, form.currency)}</strong>
          {form.payment_terms && <><span>Payment Terms</span><span>{form.payment_terms}</span></>}
          {Number(form.timeline_days) > 0 && <><span>Timeline Impact</span><span>+{form.timeline_days} days{form.new_deadline ? ` (new deadline: ${form.new_deadline})` : ''}</span></>}
        </div>

        {allCOs?.length > 0 && (
          <>
            <div className="cop-doc-divider" />
            <div className="cop-doc-section-title">CUMULATIVE CHANGE LOG</div>
            <div className="cop-doc-log">
              {allCOs.map(co => {
                const coTotal = co.pricing_type === 'hourly' ? (co.hours || 0) * (co.rate || 0) : (co.price || 0);
                return (
                  <div key={co.id} className="cop-doc-log-row">
                    <span className="cop-doc-log-num">CO-{String(co.co_number).padStart(3, '0')}</span>
                    <span className="cop-doc-log-title">{co.title}</span>
                    <span className="cop-doc-log-amt">{fmtMoney(coTotal, form.currency)}</span>
                    <span className={`cop-doc-log-status cop-doc-log-status--${co.status}`}>{co.status}</span>
                  </div>
                );
              })}
              <div className="cop-doc-log-row cop-doc-log-row--this">
                <span className="cop-doc-log-num">CO-{String(coNum).padStart(3, '0')}</span>
                <span className="cop-doc-log-title">{form.title} <em>(this CO)</em></span>
                <span className="cop-doc-log-amt">{fmtMoney(computed, form.currency)}</span>
                <span className="cop-doc-log-status cop-doc-log-status--draft">draft</span>
              </div>
              <div className="cop-doc-log-total">
                Total additional work: <strong>{fmtMoney(totalApproved + computed, form.currency)}</strong>
              </div>
            </div>
          </>
        )}

        <div className="cop-doc-divider" />
        <div className="cop-doc-sig-block">
          <div className="cop-doc-section-title">SIGNATURES</div>
          <div className="cop-doc-sig-row">
            <div className="cop-doc-sig">
              <div className="cop-doc-sig-line" />
              <div>{branding?.name || 'Freelancer'} (Contractor)</div>
            </div>
            <div className="cop-doc-sig">
              <div className="cop-doc-sig-line" />
              <div>{form.client_name || 'Client'}</div>
            </div>
          </div>
        </div>
      </div>

      <button
        className="btn btn-primary btn-lg cop-confirm-btn"
        onClick={onConfirm}
        disabled={creating || !form.title?.trim()}
      >
        {creating ? <><div className="spinner" /> Creating…</> : <><Check size={15} /> Create Change Order</>}
      </button>
    </div>
  );
}

// ── Main Panel ─────────────────────────────────────────────────────────────────
export default function ChangeOrderPanel({
  project,
  allCOs = [],
  prefill = null,       // { ledger_entry_id, title, description, client_name }
  branding = null,
  onCreated,
  onClose,
}) {
  const { addToast } = useToast();
  const { profile } = useAuth();
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [useBranding, setUseBranding] = useState(!!(branding?.branding_company_name || branding?.branding_logo_url));
  const panelRef = useRef(null);

  const [form, setForm] = useState({
    title: prefill?.title || '',
    description: prefill?.description || '',
    pricing_type: 'fixed',
    price: '',
    hours: '',
    rate: String(profile?.hourly_rate || 50),
    payment_terms: '50% upfront, 50% on delivery',
    timeline_days: '',
    new_deadline: '',
    client_name: prefill?.client_name || project?.clientName || '',
    client_company: prefill?.client_company || '',
    client_email: '',
    notes: '',
    currency: profile?.currency || 'USD',
    default_rate: profile?.hourly_rate || 50,
    base_deadline: null,
  });

  function onChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function canAdvance() {
    if (step === 0) return form.title.trim().length > 0;
    return true;
  }

  async function handleConfirm() {
    setCreating(true);
    try {
      const computed = form.pricing_type === 'hourly'
        ? (parseFloat(form.hours) || 0) * (parseFloat(form.rate) || 0)
        : parseFloat(form.price) || 0;

      const co = await createChangeOrder({
        project_id: project.id,
        ledger_entry_id: prefill?.ledger_entry_id || null,
        title: form.title,
        description: form.description,
        pricing_type: form.pricing_type,
        price: form.pricing_type === 'fixed' ? computed : null,
        hours: form.pricing_type === 'hourly' ? parseFloat(form.hours) : null,
        rate: form.pricing_type === 'hourly' ? parseFloat(form.rate) : null,
        payment_terms: form.payment_terms,
        timeline_days: parseInt(form.timeline_days) || 0,
        new_deadline: form.new_deadline || null,
        client_name: form.client_name,
        client_email: form.client_email,
        client_company: form.client_company,
        notes: form.notes,
      });

      setCreated(co);
      onCreated?.(co);
      addToast(`CO-${String(co.co_number).padStart(3, '0')} created successfully`, 'success');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to create change order', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleDownload() {
    const coForPDF = created || form;
    try {
      await generateChangeOrderPDF({
        co: { ...coForPDF, co_number: created?.co_number || (allCOs.length + 1) },
        allCOs,
        project,
        branding: useBranding ? branding : null,
      });
    } catch {
      addToast('PDF generation failed — try again', 'error');
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div className="cop-backdrop" onClick={onClose} />

      {/* Panel */}
      <div className="cop-panel" ref={panelRef} role="dialog" aria-modal="true" aria-label="Create Change Order">
        {/* Header */}
        <div className="cop-header">
          <div className="cop-header-left">
            <div className="cop-header-title">New Change Order</div>
            {project && <div className="cop-header-project">{project.projectName}</div>}
          </div>
          <button className="cop-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        {/* Steps */}
        <Steps current={step} />

        {/* Content */}
        <div className="cop-content">
          {!created ? (
            <>
              {step === 0 && <Step1 form={form} onChange={onChange} />}
              {step === 1 && <Step2 form={form} onChange={onChange} />}
              {step === 2 && (
                <Step3
                  form={form}
                  allCOs={allCOs}
                  project={project}
                  branding={branding}
                  useBranding={useBranding}
                  onToggleBranding={() => setUseBranding(v => !v)}
                  creating={creating}
                  onConfirm={handleConfirm}
                />
              )}
            </>
          ) : (
            /* Success state */
            <div className="cop-success">
              <div className="cop-success-icon"><Check size={28} /></div>
              <div className="cop-success-title">
                CO-{String(created.co_number).padStart(3, '0')} Created!
              </div>
              <p className="cop-success-desc">
                Change order saved. Download the PDF to send to your client
                or share it for signature.
              </p>
              <button className="btn btn-primary btn-lg" onClick={handleDownload}>
                <Download size={15} /> Download PDF
              </button>
              <button className="btn btn-secondary" onClick={onClose}>Done</button>
            </div>
          )}
        </div>

        {/* Footer navigation */}
        {!created && (
          <div className="cop-footer">
            <button
              className="btn btn-secondary"
              onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}
            >
              {step === 0 ? 'Cancel' : <><ChevronLeft size={14} /> Back</>}
            </button>
            {step < 2 && (
              <button
                className="btn btn-primary"
                onClick={() => setStep(s => s + 1)}
                disabled={!canAdvance()}
              >
                Continue <ChevronRight size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
